import React, { useState, useEffect, useRef } from "react"; 
import image10 from '../../../Asset/image10.png';
import imageMR from '../../../Asset/citation_image_patron.jpg';
import course from '../../../Asset/aide_repas.jpg';
import aide from '../../../Asset/aide_actes_essentiels.jpg';
import entretien from '../../../Asset/Entretien_Logement.jpg';
import mobilité from '../../../Asset/aide_mobilite.jpg';
import { Helmet } from 'react-helmet';
import { useColorMode, Box } from '@chakra-ui/react';
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion"; 
import { FaCheckCircle, FaChevronDown, FaMoon, FaShieldAlt, FaStar, FaArrowRight, FaHeart, FaHome, FaWalking, FaUtensils, FaPhoneAlt, FaEnvelope, FaQuoteLeft } from "react-icons/fa";

function Senior() {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';
    const [isHovered, setIsHovered] = React.useState(false);
    
    // États
    const [isOpen, setIsOpen] = useState(false);
    const [activeService, setActiveService] = useState(0); // <-- Nouvel état pour les solutions interactives
    const nightCareRef = useRef(null);

    // Effet "Nuit Magique" : bloque le scroll et centre l'écran sur la garde de nuit
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setTimeout(() => {
                nightCareRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300); 
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    const sectionVariants = { 
        hidden: { opacity: 0, y: 30 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } 
    };

    // Données des solutions médicalisées
    const servicesData = [
        {
            title: "Actes essentiels",
            desc: "Une assistance technique et humaine dans le plus strict respect de l'intimité de la personne.",
            icon: <FaHeart size={20} />,
            img: aide,
            list: ["Aide à la toilette", "Aide au lever/coucher", "Habillage", "Élimination"]
        },
        {
            title: "Repas & Nutrition",
            desc: "Gestion complète de l'alimentation, de l'élaboration à la surveillance médicale des régimes.",
            icon: <FaUtensils size={20} />,
            img: course,
            list: ["Courses", "Préparation sur place", "Aide à la prise", "Suivi diététique"]
        },
        {
            title: "Aide à la mobilité",
            desc: "Maintien d'une vie sociale active et sécurisation totale des transferts et déplacements.",
            icon: <FaWalking size={20} />,
            img: mobilité,
            list: ["Rendez-vous médicaux", "Balades", "Transferts sécurisés", "Transport PMR"]
        },
        {
            title: "Cadre de vie",
            desc: "Une gestion rigoureuse de l'environnement pour garantir un domicile sain, propre et apaisant.",
            icon: <FaHome size={20} />,
            img: entretien,
            list: ["Ménage soigné", "Entretien du linge", "Rangement", "Sécurisation des espaces"]
        }
    ];

    return (
        <Box position="relative" overflow="hidden" className={isDark ? 'bg-[#0a0f16]' : 'bg-[#FAFCFF]'}>
            <Helmet>
                <title>Aide à domicile pour seniors — Hetep Iaout Services</title>
                <meta name="description" content="Aide à domicile pour personnes âgées en Île-de-France : aide aux repas, toilette, mobilité, compagnie. Auxiliaires de vie diplômés, disponibles 7j/7. Devis gratuit." />
                <link rel="canonical" href="https://www.hisvie.com/seniors/" />
            </Helmet>

            {/* --- OVERLAY NUIT MAGIQUE --- */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[100] pointer-events-none overflow-hidden"
                        style={{ backgroundColor: 'rgba(5, 15, 30, 0.96)' }} 
                    >
                        {[...Array(25)].map((_, i) => (
                            <motion.div
                                key={i}
                                initial={{ y: "110vh", x: Math.random() * 100 + "vw", opacity: 0, rotate: 0 }}
                                animate={{ y: "-10vh", x: (Math.random() * 100) + "vw", opacity: [0, 0.7, 0], rotate: 360 }}
                                transition={{ duration: 10 + Math.random() * 5, repeat: Infinity, delay: Math.random() * 10, ease: "linear" }}
                                className="absolute text-yellow-400/40 text-2xl md:text-4xl"
                                style={{ left: 0, filter: "blur(1px)" }}
                            >
                                <FaMoon />
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Conteneur principal affecté par le flou de la Nuit Magique */}
            <div className={`transition-all duration-1000 ${isOpen ? 'blur-2xl grayscale opacity-30 scale-95' : ''}`}>
                
                {/* --- HERO SECTION --- */}
                <div className="relative w-full min-h-[100vh] flex items-center pt-20 overflow-hidden">
                    <motion.img 
                        initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 10, ease: "easeOut" }}
                        src={image10} alt="Accompagnement senior grande dépendance" 
                        className="absolute inset-0 w-full h-full object-cover z-0 origin-center" 
                    />
                    
                    <div className="absolute inset-0 bg-black/60 md:bg-black/0 md:bg-gradient-to-r md:from-[#0a0f16]/90 md:via-[#0a0f16]/60 md:to-black/40 z-10"></div>

                    <div className="relative z-20 w-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col lg:flex-row items-center justify-between">
                        <motion.div 
                            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }}
                            className="w-full lg:w-2/3 mt-10 lg:mt-0"
                        >
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md mb-6 md:mb-8 shadow-lg">
                                <FaShieldAlt className="text-blue-400 text-xs md:text-sm" />
                                <span className="text-white text-[10px] md:text-xs font-bold tracking-widest uppercase">Expertise Grande Dépendance</span>
                            </div>

                            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black leading-[1.1] tracking-tight mb-6 text-white">
                                Dignité & <br className="hidden md:block"/>
                                <span className="text-blue-400 font-light italic font-serif">autonomie,</span> <br/>
                                chez soi.
                            </h1>

                            <p className="text-base md:text-xl mb-10 max-w-lg leading-relaxed text-gray-300">
                                Un accompagnement médicalisé et humain pour permettre à vos proches de vieillir dans le confort et le respect de leur environnement.
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/demande-de-devis" className="w-full sm:w-auto">
                                    <motion.button 
                                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                                        className="w-full bg-blue-600 text-white font-bold py-4 px-8 rounded-2xl shadow-xl shadow-blue-600/30 hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 text-base md:text-lg"
                                    >
                                        Évaluer les besoins <FaArrowRight size={14} />
                                    </motion.button>
                                </Link>
                                <Link to="/contact" className="w-full sm:w-auto">
                                    <motion.button 
                                        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                                        className="w-full font-bold py-4 px-8 rounded-2xl transition-all border flex items-center justify-center gap-3 text-base md:text-lg bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-md"
                                    >
                                        En parler avec un conseiller
                                    </motion.button>
                                </Link>
                            </div>
                        </motion.div>

                        <motion.div 
                            initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, delay: 0.6 }}
                            className="hidden lg:flex w-2/5 flex-col items-end justify-center gap-6 mt-20"
                        >
                            <div className="p-6 rounded-[2rem] shadow-2xl border backdrop-blur-xl max-w-sm w-full bg-white/10 border-white/20">
                                <div className="flex items-start gap-4">
                                    <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center text-white shrink-0 shadow-inner">
                                        <FaStar size={24} />
                                    </div>
                                    <div>
                                        <p className="font-black text-xl mb-1 text-white">Cap'Handéo</p>
                                        <p className="text-sm leading-relaxed text-gray-200">
                                            Certification Excellence. La plus haute distinction pour l'accompagnement à domicile.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    <motion.div 
                        animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 hidden md:flex flex-col items-center gap-3 opacity-60"
                    >
                        <span className="text-[10px] font-bold tracking-widest uppercase text-white">Découvrir</span>
                        <div className="w-[2px] h-12 rounded-full bg-gradient-to-b from-white to-transparent"></div>
                    </motion.div>
                </div>

                {/* --- STATEMENT : L'EXPERTISE --- */}
                <motion.section 
                    initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={sectionVariants}
                    className="max-w-5xl mx-auto px-4 py-16 md:py-24"
                >
                    <div className="flex flex-col md:flex-row items-center md:items-start gap-8 md:gap-12">
                        <div className="relative shrink-0">
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-400/10 rounded-full blur-xl"></div>
                            <img src={imageMR} alt="Directeur" className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white/50 dark:border-gray-800 shadow-xl relative z-10" />
                        </div>
                        <div className="flex-1 text-center md:text-left pt-2">
                            <FaQuoteLeft className="text-3xl md:text-4xl text-blue-100 dark:text-gray-800 mx-auto md:mx-0 mb-4" />
                            <h3 className={`text-xl md:text-3xl font-light font-serif italic leading-relaxed mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                "Spécialiste de la grande dépendance, HIS va bien au-delà de l'assistance classique, <span className="text-blue-600 font-bold">nous pérennisons un véritable projet de vie.</span>"
                            </h3>
                            <div>
                                <p className={`font-black text-base uppercase tracking-wider ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Christian Kenne</p>
                                <p className={`text-xs md:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Directeur Général HIS</p>
                            </div>
                        </div>
                    </div>
                </motion.section>

              {/* --- NOS SOLUTIONS MÉDICALISÉES (STATIQUE & ÉDITORIAL) --- */}
                <section className="py-20 px-4 md:px-6 max-w-6xl mx-auto">
                    {/* En-tête sobre */}
                    <div className="text-center mb-20">
                        <h2 className={`text-3xl md:text-5xl font-black mb-6 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                            Nos expertises <span className="text-blue-600 font-serif italic font-light">médicalisées.</span>
                        </h2>
                        <p className={`text-base md:text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Des protocoles d'intervention rigoureux pour garantir une sécurité totale et un confort optimal à domicile.
                        </p>
                    </div>

                    {/* Liste des services en continu */}
                    <div className="space-y-24 md:space-y-32">
                        {servicesData.map((service, index) => (
                            <motion.div 
                                key={index}
                                initial="hidden"
                                whileInView="visible"
                                viewport={{ once: true, amount: 0.3 }}
                                variants={sectionVariants}
                                className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-12 md:gap-20`}
                            >
                                {/* Texte */}
                                <div className="flex-1 w-full text-left">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 flex items-center justify-center">
                                            {service.icon}
                                        </div>
                                        <h3 className={`text-2xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                            {service.title}
                                        </h3>
                                    </div>
                                    
                                    <p className={`text-base md:text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {service.desc}
                                    </p>
                                    
                                    <ul className="space-y-4 border-l-2 border-blue-600/20 pl-6">
                                        {service.list.map((item, i) => (
                                            <li key={i} className="flex items-center gap-3">
                                                <FaCheckCircle className="text-blue-600 text-xs shrink-0" />
                                                <span className={`text-sm md:text-base font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                    {item}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Image */}
                                <div className="flex-1 w-full">
                                    <div className="relative group">
                                        <div className="absolute -inset-4 bg-blue-600/5 rounded-[2rem] -z-10"></div>
                                        <img 
                                            src={service.img} 
                                            alt={service.title} 
                                            className="w-full h-[300px] md:h-[400px] object-cover rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800"
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>

               {/* --- SECTION GARDE DE NUIT (AVEC ÉTOILES, 100% STABLE) --- */}
<motion.section 
    initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={sectionVariants}
    className="py-12 md:py-24 px-4 md:px-6 max-w-7xl mx-auto"
>
    <div className="relative overflow-hidden rounded-[2.5rem] md:rounded-[3rem] bg-[#0a0f16] shadow-2xl p-8 md:p-12 lg:p-16 border border-gray-800 flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
        
        {/* Glow décoratif de nuit */}
        <div className="absolute top-0 right-0 w-[400px] md:w-[600px] h-[400px] md:h-[600px] bg-blue-800/30 rounded-full blur-[100px] pointer-events-none transform translate-x-1/3 -translate-y-1/3"></div>
        
        {/* ÉTOILES QUI SCINTILLENT */}
        {[...Array(100)].map((_, i) => (
            <motion.div
                key={`star-${i}`}
                className="absolute bg-white rounded-full pointer-events-none"
                style={{
                    width: Math.random() * 2 + 1 + 'px',
                    height: Math.random() * 2 + 1 + 'px',
                    top: Math.random() * 100 + '%',
                    left: Math.random() * 100 + '%',
                }}
                animate={{
                    opacity: [0.1, 0.9, 0.1],
                    scale: [0.8, 1.2, 0.8],
                }}
                transition={{
                    duration: Math.random() * 3 + 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: Math.random() * 2
                }}
            />
        ))}

        {/* Partie Gauche : Explication (Lune supprimée) */}
        <div className="w-full lg:w-5/12 relative z-10 text-left">
            <h2 className="text-3xl md:text-5xl font-black mb-4 md:mb-6 text-white leading-[1.1] tracking-tight">
                La sérénité, <br/>
                <span className="text-yellow-400 font-serif italic font-light">jusqu'au matin.</span>
            </h2>
            <p className="text-gray-300 text-sm md:text-lg leading-relaxed mb-8 md:mb-10 font-light">
                Pour les personnes en situation de grande dépendance, la nuit peut être une source d'angoisse. Nos intervenants assurent une présence protectrice et bienveillante pour garantir un sommeil apaisé et sécurisé.
            </p>
            <Link to="/tarifs" className="inline-block w-full sm:w-auto">
                <button className="w-full sm:w-auto px-8 py-4 bg-yellow-400 text-[#0a0f16] text-sm md:text-base rounded-xl font-bold hover:bg-yellow-300 transition-all flex items-center justify-center gap-3 shadow-lg shadow-yellow-400/20">
                    Tarifs de nuit <FaArrowRight size={14} />
                </button>
            </Link>
        </div>

        {/* Partie Droite : Les deux forfaits */}
        <div className="w-full lg:w-7/12 relative z-10 flex flex-col gap-4 md:gap-6">
            
            {/* Nuit Calme */}
            <div className="p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] bg-white/5 border border-white/10 backdrop-blur-md transition-all hover:bg-white/10">
                <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-400">
                        <FaMoon size={16} />
                    </div>
                    <h4 className="text-lg md:text-xl font-black text-white">Nuit Calme</h4>
                </div>
                <p className="text-sm md:text-base leading-relaxed text-gray-400 font-light">
                    Une présence protectrice. L'intervenant dort sur place dans une pièce séparée, mais reste disponible. Il se lève jusqu'à 3 fois par nuit pour répondre aux besoins physiologiques et rassurer votre proche.
                </p>
            </div>

            {/* Nuit Agitée */}
            <div className="p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] bg-white/5 border border-white/10 backdrop-blur-md transition-all hover:bg-white/10">
                <div className="flex items-center gap-4 mb-4">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center text-yellow-400">
                        <FaStar size={16} />
                    </div>
                    <h4 className="text-lg md:text-xl font-black text-white">Nuit Agitée</h4>
                </div>
                <p className="text-sm md:text-base leading-relaxed text-gray-400 font-light">
                    Une veille active et continue. L'intervenant reste éveillé toute la nuit pour assurer une surveillance constante (pathologies lourdes, fin de vie, angoisses nocturnes) et intervenir à tout moment.
                </p>
            </div>
        </div>
    </div>
</motion.section>

                {/* --- CTA FINAL --- */}
                <motion.section 
                    initial="hidden" whileInView="visible" viewport={{ once: true }} variants={sectionVariants}
                    className="max-w-4xl mx-auto px-4 pb-20"
                >
                    <div className={`relative overflow-hidden rounded-[2rem] p-8 md:p-12 flex flex-col md:flex-row items-center gap-8 border shadow-xl ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
                        <div className="flex-1 z-10 text-center md:text-left">
                            <h2 className={`text-2xl md:text-4xl font-black mb-3 tracking-tight ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                                Confiez-nous le <span className="text-blue-600 font-serif italic font-light">bien-être</span> de vos proches.
                            </h2>
                            <p className={`text-sm md:text-base max-w-md mx-auto md:mx-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Nos experts sont à votre écoute pour organiser une évaluation gratuite sous 48h.</p>
                        </div>

                        <div className="flex flex-col sm:flex-row w-full md:w-auto gap-3 z-10 shrink-0">
                            <Link to="/contact" className="w-full sm:w-auto">
                                <button className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3.5 rounded-xl font-bold hover:bg-blue-700 transition-all w-full shadow-md text-sm md:text-base">
                                    <FaPhoneAlt size={14} /> Nous contacter
                                </button>
                            </Link>
                            <a href="mailto:his@donnerlavieauxannees.com" className={`flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-bold transition-all border w-full sm:w-auto text-sm md:text-base ${isDark ? 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700' : 'bg-blue-50 text-blue-800 border-blue-100 hover:bg-blue-100'}`}>
                                <FaEnvelope size={14} /> Email
                            </a>
                        </div>
                    </div>
                </motion.section>
            </div>
        </Box>
    );
}

export default Senior;